import {
  Button,
  collapseColumn,
  column,
  Css,
  emptyCell,
  GridColumn,
  GridDataRow,
  GridTable,
  numericColumn,
  Palette,
  ScrollableContent,
  ScrollableFooter,
  ScrollableParent,
  simpleHeader,
  TabsWithContent,
  useModal,
} from "@homebound/beam";
import { ObjectConfig, ObjectState, useFormState } from "@homebound/form-state";
import { useEffect, useState } from "react";
import { Icon, Loading, useNavigationCheck } from "src/components";
import { NamedFragment } from "src/fragments/commonFragments.generated";
import { BidPackageStatus, SavePlanBasedBidContractLineItemInput } from "src/generated/graphqlTypes";
import { AddTradePartnerModal } from "src/routes/bids/components/AddTradePartnerModal";
import { ReviewScopeChangesCard } from "src/routes/bids/components/ReviewScopeChangesCard";
import { queryResult } from "src/utils";
import {
  applySectionSearch,
  bidPlanGroupDimensionKey,
  getSortedPofs,
  optionMatchesDimension,
  orderedBidPlanGroupDimensions,
  PlanBasedBidPackageWizard,
} from "../utils";
import { CopyCostsModal } from "./CopyCostsModal";
import { MissingOptionsCard } from "./MissingOptionsCard";
import { PlanBasedBidsTableHeader } from "./PlanBasedBidsTableHeader";
import {
  PlanBasedBidsTableV2_BidContractRevisionFragment,
  PlanBasedBidsTableV2_BidPackageBidderFragment,
  PlanBasedBidsTableV2_BidPackageFragment,
  PlanBasedBidsTableV2_BidPlanChildGroupFragment,
  PlanBasedBidsTableV2_BidPlanChildGroupLineFragment,
  PlanBasedBidsTableV2_BidPlanGroupDimensionFragment,
  PlanBasedBidsTableV2_BidPlanGroupFragment,
  PlanBasedBidsTableV2_NarrowedBidContractRevisionFragment,
  usePlanBasedBidsTableV2_BidPlanChildGroupsQuery,
} from "./PlanBasedBidsTableV2.generated";
import { PlanBasedScopeTable } from "./PlanBasedScopeTable";
import {
  CostInput,
  tradeColumnDescriptors,
  tradeColumnHeader,
  tradeCostCell,
  TradeCostColumnsData,
} from "./planBasedTradeColumns";

/** Holds each POF's data as the user opens its tab, so we don't have to load them all up front. */
type LoadedByPof = Record<string, readonly PlanBasedBidsTableV2_BidPlanGroupFragment[]>;

type PlanBasedBidsTableV2Props = {
  bidPackage: PlanBasedBidsTableV2_BidPackageFragment;
  searchFilter?: string;
  showDownloadButton?: boolean;
  scopeView?: "hide" | "all" | "changes";
  initiallySelectedTab?: string;
  onSelectedTabChange?: (pofId: string) => void;
  /** Set when rendered inside the create/edit wizard, so the missing options card writes through its form. */
  wizard?: PlanBasedBidPackageWizard;
} & (
  | {
      mode?: "view";
      /** If set, narrow the table to just the matching BCR and its bidder. */
      bidContractRevision?: PlanBasedBidsTableV2_NarrowedBidContractRevisionFragment;
      onSave?: undefined;
      onCancel?: undefined;
    }
  | {
      mode: "edit";
      /** Required in edit mode: identifies the BCR (and bidder) whose costs are being edited. */
      bidContractRevision: PlanBasedBidsTableV2_NarrowedBidContractRevisionFragment;
      onSave: (input: SavePlanBasedBidContractLineItemInput[]) => void | Promise<void>;
      onCancel: () => void;
    }
);

export function PlanBasedBidsTableV2(props: PlanBasedBidsTableV2Props) {
  const {
    bidPackage,
    searchFilter,
    bidContractRevision,
    scopeView = "hide",
    wizard,
    initiallySelectedTab,
    onSelectedTabChange,
  } = props;
  const mode = props.mode ?? "view";
  const sortedPofs = getSortedPofs(bidPackage);
  const { openModal } = useModal();
  const [selectedTab, setSelectedTab] = useState<string>(
    initiallySelectedTab && sortedPofs.some((pof) => pof.id === initiallySelectedTab)
      ? initiallySelectedTab
      : sortedPofs[0].id,
  );
  const showScope = scopeView === "all" || scopeView === "changes";
  const query = usePlanBasedBidsTableV2_BidPlanChildGroupsQuery({
    variables: {
      bidPackageId: bidPackage.id,
      productOfferingId: selectedTab,
      showScope,
      showChangesOnly: scopeView === "changes",
    },
  });

  // Single formState across all POF tabs. The table query still fires per active
  // tab, but each tab's `bidPlanGroups` are merged into `loadedByPof` and passed
  // as form-state's reactive `init.input`. form-state diffs the cost list by id,
  // so user edits survive when a new tab loads and the input grows.
  const [loadedByPof, setLoadedByPof] = useState<LoadedByPof>({});
  useEffect(() => {
    if (mode !== "edit") return;
    const data = query.data?.bidPackage.bidPlanGroups;
    const pofId = query.variables?.productOfferingId;
    if (!data || !pofId) return;
    setLoadedByPof((prev) => (prev[pofId] === data ? prev : { ...prev, [pofId]: data }));
  }, [mode, query]);

  const formState = useFormState({
    readOnly: mode !== "edit",
    config: editFormConfig,
    init: {
      input: loadedByPof,
      map: (loaded) => buildEditCosts(loaded, bidContractRevision?.id, bidPackage.costCodes),
    },
  });

  // Warn before leaving the page with unsaved costs.
  const { useRegisterNavigationCheck } = useNavigationCheck();
  useRegisterNavigationCheck(() => mode !== "edit" || !formState.dirty, [mode, formState]);

  async function handleSave() {
    await props.onSave?.(buildSaveInput(formState));
    formState.commitChanges();
  }

  return (
    <>
      <ScrollableParent>
        <ScrollableContent virtualized>
          {mode === "edit" && sortedPofs.length > 1 && (
            <div css={Css.df.jcfe.$}>
              <Button
                label="Copy Costs"
                variant="text"
                icon="duplicate"
                onClick={() =>
                  openModal({
                    content: (
                      <CopyCostsModal
                        bidPackage={bidPackage}
                        bidContractId={bidContractRevision!.bidContract.id}
                        sourceProductOfferingId={selectedTab}
                        formState={formState}
                      />
                    ),
                  })
                }
              />
            </div>
          )}
          {/* If we are looking at a specific BCR, don't show the missing options card */}
          {bidContractRevision ? null : <MissingOptionsCard bidPackage={bidPackage} wizard={wizard} />}
          <TabsWithContent
            alwaysShowAllTabs
            tabs={sortedPofs.map((pof) => ({
              name: pof.name,
              value: pof.id,
              // Flag tabs whose plan has pending scope updates
              endAdornment: bidPackage.availableUpdates.some((u) => u.source.readyPlan.id === pof.id) ? (
                <span css={Css.dif.aic.jcc.br100.bgBlue50.hPx(20).wPx(20).$}>
                  <Icon icon="refresh" inc={2} color={Palette.Blue700} />
                </span>
              ) : undefined,
              render: () => {
                if (query.loading) return <Loading />;
                return queryResult(query, ({ bidPackage: { bidPlanGroups, scopedLineItems } }) => (
                  <>
                    <ReviewScopeChangesCard
                      bidPackageId={bidPackage.id}
                      productOfferingId={pof.id}
                      planName={pof.displayName}
                      updates={bidPackage.availableUpdates.filter((u) => u.source.readyPlan.id === pof.id)}
                    />
                    {showScope ? (
                      <PlanBasedScopeTable
                        bidPackage={bidPackage}
                        bidPlanGroups={bidPlanGroups}
                        scopedLineItems={scopedLineItems ?? []}
                        mode={mode}
                        formState={mode === "edit" ? formState : undefined}
                        bidContractRevision={bidContractRevision}
                        searchFilter={searchFilter}
                      />
                    ) : (
                      <PlanBasedBidsTables
                        bidPackage={bidPackage}
                        bidPlanGroups={bidPlanGroups}
                        productOffering={pof}
                        searchFilter={searchFilter}
                        mode={mode}
                        formState={mode === "edit" ? formState : undefined}
                        bidContractRevision={bidContractRevision}
                      />
                    )}
                  </>
                ));
              },
            }))}
            selected={selectedTab}
            onChange={(pofId) => {
              setSelectedTab(pofId);
              onSelectedTabChange?.(pofId);
            }}
          />
        </ScrollableContent>
      </ScrollableParent>
      {mode === "edit" && (
        <ScrollableFooter>
          <div css={Css.df.px6.py3.bshHover.bgWhite.$}>
            <div css={Css.mla.df.gap2.$}>
              <Button size="lg" onClick={() => props.onCancel?.()} label="Cancel" variant="quaternary" />
              <Button size="lg" onClick={handleSave} label="Save" />
            </div>
          </div>
        </ScrollableFooter>
      )}
    </>
  );
}

type PlanBasedBidsTablesProps = {
  bidPackage: PlanBasedBidsTableV2_BidPackageFragment;
  bidPlanGroups: PlanBasedBidsTableV2_BidPlanGroupFragment[];
  productOffering: NamedFragment;
  searchFilter?: string;
  mode: "view" | "edit";
  formState: ObjectState<EditFormValues> | undefined;
  bidContractRevision: PlanBasedBidsTableV2_NarrowedBidContractRevisionFragment | undefined;
};

function PlanBasedBidsTables(props: PlanBasedBidsTablesProps) {
  const { bidPackage, bidPlanGroups, productOffering, searchFilter, mode, formState, bidContractRevision } = props;
  const { openModal } = useModal();
  const activeBidders = bidPackage.activeBidders;
  // Hide the inline Add Trade link in the create flow (status === Draft) — the bid
  // package isn't open for bidding yet, so adding a trade isn't actionable.
  const canAddTrade = mode === "view" && activeBidders.isEmpty && bidPackage.status.code !== BidPackageStatus.Draft;
  // When narrowed to a specific BCR, render only the bidder whose BC matches that BCR.
  const bidders = bidContractRevision
    ? activeBidders.filter((b) => b.bidContract?.id === bidContractRevision.bidContract.id)
    : activeBidders;
  const bcrs = bidContractRevision ? [bidContractRevision] : bidPackage.latestVersion.latestBidContractRevisions;
  function costRowLookup(bpglId: string, costCodeId: string) {
    return formState?.costs.rows.find((r) => r.value.groupLineId === bpglId && r.value.costCodeId === costCodeId);
  }
  // In view mode, when narrowed to a specific BCR (e.g. viewing an older signed version),
  // only render lines that had a cost saved on that revision. Lines added in a later
  // version shouldn't show up in the historical view. In edit mode we keep every line so
  // the user can enter costs against any of them.
  const rowFilterBcr = mode === "view" ? bidContractRevision : undefined;
  const sections = bidPlanGroups.sortByKey("order").flatMap((bpg) =>
    bpg.childGroups.map((bpcg) => {
      const rows = createRows(bpg, bpcg, rowFilterBcr);
      const columns = createColumns({
        bidPlanGroup: bpg,
        productOffering,
        groupDisplayName: bpcg.displayName,
        activeBidders: bidders,
        bidContractRevisions: bcrs,
        costCodes: bidPackage.costCodes,
        addTradeAction: canAddTrade
          ? () => openModal({ content: <AddTradePartnerModal bidPackage={bidPackage} /> })
          : undefined,
        mode,
        costRowLookup,
        // Only add the collapse column when at least one row actually nests children.
        hasCollapsibleRows: rows.some((r) => !!r.children?.nonEmpty),
      });
      return { bpg, bpcg, rows, columns, search: applySectionSearch(searchFilter, bpcg.displayName, rows, columns) };
    }),
  );
  const visibleSections = sections.filter((s) => !s.search.hidden);
  return (
    <>
      {visibleSections.map(({ bpg, bpcg, rows, columns, search }) => (
        <div css={Css.mb2.$} key={bpcg.id}>
          <PlanBasedBidsTableHeader bidPlanGroup={bpg} displayName={bpcg.displayName} />
          <GridTable columns={columns} filter={search.tableFilter} rows={rows} id={bpcg.id} />
        </div>
      ))}
      {sections.nonEmpty && visibleSections.isEmpty && <NoSearchResults searchFilter={searchFilter} />}
    </>
  );
}

type HeaderRow = { kind: "header"; data: undefined };
// Display the first dimension's option name (e.g. "Scheme A") as the parent of the cost lines.
type ParentRow = { kind: "parent"; data: { name: string } };
type CostLineRow = {
  kind: "costLine";
  data: {
    bidPlanChildGroupLine: PlanBasedBidsTableV2_BidPlanChildGroupLineFragment;
    display: Record<string, string>;
    isNoCost: boolean;
  };
};
type Row = HeaderRow | ParentRow | CostLineRow;

// Layout: collapse + dimension columns + flex spacer + (per-bidder × cost-code columns | Add Trade link).
// The bidder name is stacked above the cost code in the first cost-code column of each bidder;
// subsequent columns show only the cost code. Cell costs come from PB-BCLIs joined to the bidder's
// BCR (matched by bidContract.id), so an unsubmitted request renders empty cells.
function createColumns(props: {
  bidPlanGroup: PlanBasedBidsTableV2_BidPlanGroupFragment;
  productOffering: NamedFragment;
  groupDisplayName: string;
  activeBidders: PlanBasedBidsTableV2_BidPackageBidderFragment[];
  bidContractRevisions: PlanBasedBidsTableV2_BidContractRevisionFragment[];
  costCodes: NamedFragment[];
  addTradeAction: (() => void) | undefined;
  mode: "view" | "edit";
  costRowLookup: (bpglId: string, costCodeId: string) => ObjectState<CostInput> | undefined;
  hasCollapsibleRows: boolean;
}): GridColumn<Row>[] {
  const {
    bidPlanGroup,
    productOffering,
    groupDisplayName,
    activeBidders,
    bidContractRevisions,
    costCodes,
    addTradeAction,
    mode,
    costRowLookup,
    hasCollapsibleRows,
  } = props;
  const hasTrades = activeBidders.nonEmpty;

  // When the group has no dimensions the backend emits a single options=[] line so the
  // group renders as one row labeled with the product offering's name (a "base only" cost).
  const dimensionColumns = bidPlanGroup.dimensions.isEmpty
    ? [column<Row>({ header: "Plan", parent: emptyCell, costLine: productOffering.name, mw: "300px" })]
    : orderedBidPlanGroupDimensions(bidPlanGroup.dimensions).map((dim, idx) => {
        return column<Row>({
          // If the group's title is derived from an option, don't repeat it in the dimension.
          header: dim.name === groupDisplayName ? "Options" : dim.name,
          // The first dimension column is the parent row label for the group rows.
          parent: idx === 0 ? ({ name: parentName }) => parentName : emptyCell,
          costLine: ({ display }) => display[bidPlanGroupDimensionKey(dim)] ?? emptyCell,
          mw: "300px",
        });
      });

  const spacerColumn = column<Row>({ header: emptyCell, parent: emptyCell, costLine: emptyCell, w: 1 });

  // Default collapseColumn renders a chevron on rows that have children; drop it entirely when nothing nests.
  const collapseCol = hasCollapsibleRows ? [collapseColumn<Row>()] : [];

  if (!hasTrades) {
    // Once any request exists this column disappears in favor of the (request × costCode) leaf columns below
    return [
      ...collapseCol,
      ...dimensionColumns,
      spacerColumn,
      column<Row>({
        header: () =>
          addTradeAction ? <Button label="Add Trade" variant="text" onClick={addTradeAction} /> : emptyCell,
        parent: emptyCell,
        costLine: emptyCell,
        w: "300px",
      }),
    ];
  }

  // The per-(bidder × cost code) cost columns. Their header + cost-cell logic is shared with the scope table
  // (see planBasedTradeColumns) so a user can enter/view costs in either view; here we just wire it to this
  // table's row kinds.
  const tradeCostContext: TradeCostColumnsData = {
    activeBidders,
    bidContractRevisions,
    costCodes,
    mode,
    costRowLookup,
  };
  const tradeColumns = tradeColumnDescriptors(tradeCostContext).map((d) =>
    numericColumn<Row>({
      header: tradeColumnHeader(d),
      parent: emptyCell,
      costLine: ({ bidPlanChildGroupLine, isNoCost }) =>
        tradeCostCell(bidPlanChildGroupLine, d, tradeCostContext, isNoCost),
      w: "300px",
    }),
  );

  return [...collapseCol, ...dimensionColumns, spacerColumn, ...tradeColumns];
}

// Simple header followed by one collapsible parent row per first-dimension option (e.g. Elevation).
// The parent row is just a label; its cost lines nest as children.
// When `bcr` is passed, lines are derived from BCR -> PB-BCLI -> ChildLine so a historical
// version only renders lines that actually had a cost on that revision. Lines archived from
// the BP after this revision was signed still render via the BCR's PB-BCLIs.
function createRows(
  bidPlanGroup: PlanBasedBidsTableV2_BidPlanGroupFragment,
  childGroup: PlanBasedBidsTableV2_BidPlanChildGroupFragment,
  bcr: PlanBasedBidsTableV2_NarrowedBidContractRevisionFragment | undefined,
): GridDataRow<Row>[] {
  const lines = bcr
    ? bcr.planBasedLineItems
        .filter((pbbcli) => pbbcli.groupLine.group.id === childGroup.id)
        .map((pbbcli) => pbbcli.groupLine)
        .uniqueByKey("id")
    : childGroup.lines;
  const firstDimension = orderedBidPlanGroupDimensions(bidPlanGroup.dimensions)[0];

  // No dimensions: the group is a single "base only" line, so render it flat under the header.
  if (!firstDimension) {
    return [simpleHeader, ...buildCostLineRows(bidPlanGroup.dimensions, lines, bidPlanGroup.isNoCost)];
  }

  // Otherwise group the lines by their first-dimension option and nest each group under a parent.
  const groups = lines.groupByObject(
    (bpcgl) => bpcgl.options.find((o) => optionMatchesDimension(o.globalOption, firstDimension))?.globalOption,
  );
  const parentRows = groups.flatMap(([parentGo, bpcgls]) => {
    const costLines = buildCostLineRows(bidPlanGroup.dimensions, bpcgls, bidPlanGroup.isNoCost);
    // A group with a single line needs no parent header or collapse indicator, so we should render it flat.
    return costLines.length === 1
      ? costLines
      : [
          {
            kind: "parent" as const,
            id: `parent:${parentGo?.id}`,
            data: { name: parentGo?.name ?? "" },
            children: costLines,
          },
        ];
  });

  return [simpleHeader, ...parentRows];
}

// Build the cost line row for each child group line.
function buildCostLineRows(
  dimensions: PlanBasedBidsTableV2_BidPlanGroupDimensionFragment[],
  bpcgls: PlanBasedBidsTableV2_BidPlanChildGroupLineFragment[],
  isNoCost: boolean,
): GridDataRow<Row>[] {
  return bpcgls.map((bpcgl) => {
    // Key each option by the dimension it fills so the columns (also keyed by dimension) can look it up.
    const display = bpcgl.options.mapToObject((rpo) => {
      const dim = dimensions.find((d) => optionMatchesDimension(rpo.globalOption, d));
      return [dim ? bidPlanGroupDimensionKey(dim) : rpo.globalOption.type.id, rpo.globalOption.name];
    });
    return { kind: "costLine", id: bpcgl.id, data: { bidPlanChildGroupLine: bpcgl, display, isNoCost } };
  });
}

export type EditFormValues = { costs: CostInput[] };

export const editFormConfig: ObjectConfig<EditFormValues> = {
  costs: {
    type: "list",
    // update: incremental since we want only the rows the user touched on changedValue
    update: "incremental",
    config: {
      id: { type: "value" },
      pbbcliId: { type: "value" },
      groupLineId: { type: "value" },
      costCodeId: { type: "value" },
      totalCostInCents: { type: "value" },
    },
  },
};

// Map the dirty cost rows to the save input (a row is dirty if the user changed
// totalCostInCents). Read full row.value rather than `changedValue` because we always
// need groupLineId/costCodeId on the wire, not just dirty fields.
export function buildSaveInput(formState: ObjectState<EditFormValues>): SavePlanBasedBidContractLineItemInput[] {
  return formState.costs.rows
    .filter((r) => r.dirty)
    .map((r) => ({
      id: r.value.pbbcliId,
      totalCostInCents: r.value.totalCostInCents,
      costCodeId: r.value.costCodeId,
      groupLineId: r.value.groupLineId,
    }));
}

// Build the cost list (one per BPGL × CostCode) from every PO's loaded bidPlanGroups.
// Existing PB-BCLIs (matched by `(bcr.id, costCode.id)`) seed their `pbbcliId` and current
// `totalCostInCents`; missing ones get a synthetic id so form-state can still match the
// row across re-inits when more tabs load.
function buildEditCosts(
  loaded: LoadedByPof,
  bcrId: string | undefined,
  costCodes: readonly NamedFragment[],
): EditFormValues {
  const bpcgls = loaded
    .toValues()
    .flatMap((bidPlanGroups) => bidPlanGroups.flatMap((bpg) => bpg.childGroups))
    .flatMap((bpcg) => bpcg.lines);

  const costs = bpcgls.flatMap((bpcgl) =>
    costCodes.map((cc) => {
      const pbbcli = bcrId
        ? bpcgl.planBasedBidContractLineItems.find((p) => p.revision.id === bcrId && p.costCode.id === cc.id)
        : undefined;
      return {
        id: pbbcli?.id ?? `${bpcgl.id}:${cc.id}`,
        pbbcliId: pbbcli?.id,
        groupLineId: bpcgl.id,
        costCodeId: cc.id,
        totalCostInCents: pbbcli?.totalCostInCents,
      };
    }),
  );
  return { costs };
}

export function NoSearchResults(props: { searchFilter: string | undefined }) {
  const { searchFilter } = props;
  return (
    <div css={Css.tac.py4.gray700.$} data-testid="noResults">
      No results for "{searchFilter?.trim()}"
    </div>
  );
}
